/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useMatches } from 'react-router';

/**
 * Page-level UI configuration, declared per-route via the `handle.ui` export.
 *
 * Any route may set this (e.g. cart, product detail, home). The shell
 * (`routes/_app.tsx`) reads it during render and reflects it onto `<main>` as
 * `data-*` attributes, so the correct layout is present in the SERVER-rendered
 * HTML — no post-hydration mutation, no layout shift (CLS).
 *
 * `data-has-top-padding` IS WIRED — `theme/overrides/app-shell.css` selects on it
 * and is the only thing that puts top padding on `<main>`. It was declared and
 * read by no CSS from its introduction until 2026-08-19, while
 * `routes/_app.cart.tsx` documented it as a working lever and `<main>` carried
 * an unconditional `pt-8`; `__tests__/declared-never-read.test.ts` is the gate
 * that exists because of it. Do not reintroduce a hard-coded `pt-*` on `<main>`
 * — that is what made the attribute inert.
 *
 * `data-hero-bleed` is still read by nothing (it remains on that gate's
 * baseline).
 *
 * @example
 * ```tsx
 * // In a route module — opt OUT of the shell's top padding because the page
 * // supplies its own (routes/_app.search.tsx does exactly this).
 * export const handle = {
 *   ui: { main: { hasTopPadding: false } }
 * };
 * ```
 */
export interface PageUIConfig {
    /** Header configuration for this page. */
    header?: {
        /**
         * When true, the route opts its `<main>` out of the header-height top
         * padding so a hero can bleed full-bleed behind a transparent header
         * (reflected as `data-hero-bleed="true"` on `<main>`).
         *
         * @default false
         */
        transparentOnLoad?: boolean;
        /**
         * When true, the route asks for the header's LEFT CLUSTER (the store
         * link stacked above the gender tabs) to carry an extra left inset
         * (reflected as `data-header-left-inset="true"` on `<main>`, consumed by
         * `theme/overrides/header.css`).
         *
         * DEFAULTS TO FALSE, and the stylesheet reads the value through a
         * `var(--header-left-cluster-inset, 0px)` fallback rather than a shared
         * declaration — so a route that says nothing, and a brand that was never
         * measured, are inert BY CONSTRUCTION rather than by care.
         *
         * WHY IT EXISTS. Live NM's header geometry is a property of the TEMPLATE
         * the page renders from; ours is route-invariant. Live's category
         * template insets that cluster ~16px where its search template does not.
         * The full evidence, the measured ink table, and an explicit statement
         * that we consider live's choice questionable and are matching it
         * deliberately, all live on the rule in `theme/overrides/header.css` —
         * next to the value, where the next person to touch it will be.
         *
         * @default false
         */
        insetLeftCluster?: boolean;
    };
    /** Main content area configuration for this page. */
    main?: {
        /**
         * Whether `<main>` carries the shell's standard top padding
         * (`--ui-main-top-padding`, 2rem — the value `<main>` used to hard-code
         * as `pt-8`). Reflected as `data-has-top-padding` on `<main>` and
         * applied by `theme/overrides/app-shell.css`.
         *
         * DEFAULTS TO TRUE, so a route that declares nothing renders exactly
         * what it rendered when the padding was an unconditional class. Set it
         * to `false` on a page that supplies its own top space — the SRP does,
         * because live NM reserves 11px there at 390 against the shell's 32.
         *
         * @default true
         */
        hasTopPadding?: boolean;
    };
    /** Promotional ticker band configuration for this page. */
    ticker?: {
        /**
         * Whether the shell mounts `<SiteTicker>` above `<header>` on this page.
         *
         * DEFAULTS TO TRUE, so a route that declares nothing renders exactly what
         * it rendered when the mount was unconditional. Set it to `false` on a
         * page live does not carry the band on.
         *
         * MEASURED, 2026-08-21 — the cart is the one page that sets it. Live
         * carries no ticker on `/cart` on either brand, and the search reaching
         * that conclusion has a positive control that PASSES: the PLP captures DO
         * record ticker copy (`.reference/captures/nm-plp-d-1280/App.tsx:27`
         * `LAST CALL®: EXTRA 25% OFF SALE STYLES`; `bg-plp-d-1280` carries four
         * rotating messages), so the capture tool records tickers when they are
         * there. The cart captures carry none — `nm-cart-d-1280`'s first text is
         * the header, `nm-cart-m-375`'s is the black promo band, `bg-cart-d-1280`'s
         * is `Search`, `bg-cart-m-375`'s is the heading — and both owner full-page
         * PNGs (`owner-live-{nm,bg}-cart-1280-fullpage.png`) start at the header
         * with nothing above it.
         *
         * The visible symptom this removes is NM-specific and is the reason it is
         * ranked where it is: our ticker (44px at desktop, 60 at 390) sat directly
         * above the NM cart's own black `SPECIAL OFFER` promo band, so the page
         * opened on TWO stacked full-width black bands where live has one.
         *
         * @default true
         */
        visible?: boolean;
    };
}

/**
 * Read the leaf route's `handle.ui` config. Backed by `useMatches`, which
 * resolves on the server too, so callers can apply the result during render
 * (SSR) rather than in an effect.
 *
 * @returns the leaf route's `PageUIConfig`, or `{}` if none is declared.
 */
export function usePageUIConfig(): PageUIConfig {
    const matches = useMatches();
    const leafMatch = matches[matches.length - 1];

    const handle = leafMatch?.handle as { ui?: PageUIConfig } | undefined;
    return handle?.ui ?? {};
}

/**
 * Map a `PageUIConfig` to the `data-*` attributes that belong on `<main>`.
 * Returned as a plain props object so the shell can spread it onto `<main>` at
 * render time:
 *
 * ```tsx
 * <main className="grow" {...mainPaddingDataAttributes(usePageUIConfig())}>
 * ```
 *
 * Both attributes are always emitted (`"true"`/`"false"`) so the served HTML is
 * deterministic and SPA navigations that flip the value update it on the
 * existing attribute rather than adding/removing it.
 *
 * `hasTopPadding` is read as `!== false` rather than as a truthy test: the
 * padding is the shell default, so `undefined` (no `handle.ui`) must resolve to
 * `"true"`. A truthy test would silently strip 32px from every route that
 * declares nothing, which is every route but two.
 *
 * THE NAME IS NARROWER THAN THE FUNCTION, deliberately. `data-header-left-inset`
 * is not a padding lever — it is a header geometry lever that happens to land on
 * the same element, because `<main>` is the only node the app shell renders that
 * a route-level config can be stamped on. The name is kept rather than widened
 * because `theme/overrides/app-shell.css` cites it by name in a comment and that
 * file is outside this change; renaming here would leave a dangling reference
 * there. Read this as "the data attributes the shell stamps on `<main>`".
 */
export function mainPaddingDataAttributes(config: PageUIConfig): {
    'data-has-top-padding': 'true' | 'false';
    'data-hero-bleed': 'true' | 'false';
    'data-header-left-inset': 'true' | 'false';
} {
    return {
        'data-has-top-padding': config.main?.hasTopPadding === false ? 'false' : 'true',
        'data-hero-bleed': config.header?.transparentOnLoad ? 'true' : 'false',
        // Emitted as "true"/"false" on every route for the same reason as the two
        // above: a deterministic served attribute, so an SPA navigation that flips
        // the value updates an existing attribute rather than adding or removing
        // one. Only `"true"` is selected for; `"false"` matches no rule.
        'data-header-left-inset': config.header?.insetLeftCluster ? 'true' : 'false',
    };
}
